<template>
  <v-card 
    class="evento-card" 
    :class="{ 'evento-pasado': pasado }"
    :elevation="pasado ? 2 : 8"
    hover
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="image-container">
      <v-img 
        :src="val.image_full_path" 
        height="280"
        class="evento-image"
        :class="{ 'image-hover': hovered }"
        @click="showBanner(val.banner_full_path)"
      >
        <template v-slot:placeholder>
          <v-layout fill-height align-center justify-center ma-0>
            <v-progress-circular indeterminate color="orange"></v-progress-circular>
          </v-layout>
        </template>
        <div class="image-overlay">
          <v-chip 
            class="date-badge" 
            color="orange" 
            dark 
            small
            outlined
          >
            <v-icon small left>event</v-icon>
            {{ val.fecha_hora | formatDateStringShort }}
          </v-chip>
        </div>
        <v-fade-transition>
          <div v-if="hovered" class="zoom-button" @click.stop="showBanner(val.image_full_path)">
            <v-btn icon dark large color="rgba(0,0,0,0.6)">
              <v-icon>zoom_in</v-icon>
            </v-btn>
          </div>
        </v-fade-transition>
      </v-img>
    </div>

    <v-card-title class="px-4 pt-4 pb-2">
      <div class="title-section">
        <h3 class="evento-title">{{ val.nombre }}</h3>
        <div class="evento-location">
          <v-icon small color="grey darken-1">place</v-icon>
          <span>{{ val.ciudad }}</span>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="px-4 pt-2 pb-3">
      <div class="modalidades-section mb-3">
        <v-chip 
          v-for="mod in val.modalidades" 
          :key="mod.id"
          small
          color="orange lighten-4"
          text-color="orange darken-2"
          class="mr-1 mb-1"
        >
          {{ mod.nombre }}
        </v-chip>
        <v-chip
          v-if="!pasado"
          class="payment-deadline-pill ml-1 mb-1"
          small
          outlined
        >
          <v-icon small left>schedule</v-icon>
          {{ plazoParaPagarTexto }}
        </v-chip>
      </div>

      <div class="info-section">
        <div class="info-item">
          <v-icon small color="orange" class="info-icon">calendar_today</v-icon>
          <span class="info-label">Fecha:</span>
          <span class="info-value">{{ val.fecha_hora | formatDateString }}</span>
        </div>
        <div class="info-item">
          <v-icon small color="orange" class="info-icon">access_time</v-icon>
          <span class="info-label">Hora:</span>
          <span class="info-value">{{ val.fecha_hora | formatTime }}</span>
        </div>
        <div class="info-item">
          <v-icon small color="orange" class="info-icon">location_on</v-icon>
          <span class="info-label">Lugar:</span>
          <span class="info-value">{{ val.lugar }}</span>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="px-3 px-md-4 pb-3 pb-md-4 pt-0 card-actions-touch">
      <v-layout column class="actions-layout">
        <template v-if="!pasado">
          <router-link
            :to="{ name: 'informacionEvento', params: { id: val.id } }"
            class="mb-2 mb-md-3 action-button action-button-mas-info action-button-link"
          >
            <v-icon left>info</v-icon>
            Ver detalles e inscribirme
          </router-link>
          
          <template v-if="showInscribirseButtonOnCard">
            <v-btn 
              v-if="val.inscripcion_abierta"
              block 
              dark
              color="orange" 
              large
              class="action-button primary-action inscription-button"
              :to="{ name: 'inscripcion', params: { id: val.id } }"
            >
              <v-icon left>how_to_reg</v-icon>
              Inscribirse
            </v-btn>
            
            <v-btn 
              v-else-if="inscripcionPasada(val)"
              block 
              disabled
              outlined
              color="red"
              large
              class="action-button"
              style="margin-top: 10px !important;"
            >
              <v-icon left>block</v-icon>
              Inscripción finalizada
            </v-btn>
            
            <v-btn 
              v-else-if="inscripcionProxima(val)"
              block 
              disabled
              outlined
              color="orange"
              large
              class="action-button"
            >
              <v-icon left>schedule</v-icon>
              Inscripción próximamente
            </v-btn>
          </template>
        </template>

        <template v-if="pasado">
          <v-btn 
            v-if="hasLinks(val)"
            block 
            dark
            color="orange" 
            large
            class="action-button"
            :to="{ name: 'resultadosFotosEvento', params: { id: val.id } }"
          >
            <v-icon left>photo_library</v-icon>
            Ver resultados y fotos
          </v-btn>
        </template>
      </v-layout>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  data() {
    return {
      hovered: false
    }
  },
  props: {
    val: Object,
    pasado: Boolean,
    /** Si true, muestra el botón "Inscribirse" en la card. Por defecto false para que el usuario entre a "Más información" primero. */
    showInscribirseButtonOnCard: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isTouchDevice() {
      return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    },
    plazoParaPagarTexto() {
      const horas = this.val.duracion_inscripcion_pendiente
      if (horas != null && horas !== '') {
        return `Plazo de pago: ${horas}h`
      }
      return 'Plazo para pagar'
    }
  },
  methods: {
    onMouseEnter() {
      if (!this.isTouchDevice) this.hovered = true;
    },
    onMouseLeave() {
      if (!this.isTouchDevice) this.hovered = false;
    },
    showBanner(src) {
      this.$emit('showBanner', src);
    },
    inscripcionProxima(event) {
      return new Date() < new Date(event.fecha_inicio_inscripcion)
    },

    inscripcionPasada(event) {
      return new Date() > new Date(event.fin_inscripcion)
    },
    hasLinks(event) {
      const { event_result_links, photo_album_links } = event;
      const jsonLinks = event_result_links ? JSON.parse(event_result_links) : []
      const jsonPhotos = photo_album_links ? JSON.parse(photo_album_links) : []
      return (jsonLinks && jsonLinks.length > 0) || (jsonPhotos && jsonPhotos.length > 0)
    }
  }
}
</script>

<style>
.date-badge{
  padding: 0 30px !important;
}

</style>

<style scoped>
.actions-layout .action-button{
  margin: 0 !important
}

.actions-layout .inscription-button {
  margin-top: 10px !important;
}

.v-chip--small {
  height: 36px !important;
}

.evento-card {
  border-radius: 16px !important;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.evento-card:hover {
  transform: translateY(-4px);
}

/* Hover solo con mouse, no en touch (evita que el primer tap “active” hover y parezca que pide doble tap) */
@media (hover: none) {
  .evento-card:hover {
    transform: none;
  }
}

.evento-pasado {
  opacity: 0.85;
}

/* Evitar doble tap en mobile: el primer toque debe navegar */
.card-actions-touch {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.action-button-mas-info {
  touch-action: manipulation;
  cursor: pointer;
}

/* Router-link con aspecto de botón (evita doble tap en mobile) */
.action-button-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 12px 20px;
  margin-bottom: 8px;
  border: 2px solid #ff9800;
  border-radius: 12px;
  color: #ff9800;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-decoration: none;
  text-transform: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.action-button-link:hover {
  background: rgba(255, 152, 0, 0.08);
  color: #f57c00;
}

.action-button-link .v-icon {
  color: inherit !important;
  margin-right: 8px;
  font-size: 20px !important;
}

.image-container {
  position: relative;
  overflow: hidden;
}

.evento-image {
  cursor: pointer;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.image-hover {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
}

.date-badge {
  font-weight: 600;
  letter-spacing: 0.5px;
  backdrop-filter: blur(10px);
  background-color: rgba(255, 73, 0, 0.9) !important;
  padding: 0 30px !important;
}

.zoom-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}

.title-section {
  width: 100%;
}

.evento-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  color: #212121;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.evento-location {
  display: flex;
  align-items: center;
  color: #757575;
  font-size: 0.9rem;
  gap: 4px;
}

.modalidades-section {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.payment-deadline-pill {
  background-color: #e74c3c !important;
  border-color: #c0392b !important;
  color: #f5f6fa !important;
  font-size: 0.75rem;
  font-weight: 500;
  height: 24px;
}

.payment-deadline-pill .v-icon {
  font-size: 14px;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9rem;
}

.info-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.info-label {
  font-weight: 600;
  color: #616161;
  min-width: 60px;
}

.info-value {
  color: #212121;
  flex: 1;
}

.actions-layout {
  width: 100%;
  gap: 0;
}

.action-button {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 12px;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 48px;
  padding: 12px 20px !important;
  font-size: 1rem;
  margin: 0;
}

.action-button .v-icon {
  font-size: 20px !important;
  margin-right: 8px;
}

.primary-action {
  box-shadow: 0 4px 12px rgba(255, 73, 0, 0.3);
}

.primary-action:hover {
  box-shadow: 0 6px 16px rgba(255, 73, 0, 0.4);
  transform: translateY(-1px);
}

.evento-title {
  font-size: 1.25rem;
}

.info-item {
  font-size: 0.9rem;
}

@media (min-width: 600px) {
  .evento-title {
    font-size: 1.5rem;
  }
  
  .info-item {
    font-size: 0.9rem;
  }
  
  .action-button {
    min-height: 50px;
    padding: 14px 24px !important;
    font-size: 1.05rem;
  }
  
  .action-button .v-icon {
    font-size: 22px !important;
  }
}

@media (min-width: 960px) {
  .action-button {
    min-height: 52px;
    padding: 14px 28px !important;
    font-size: 1.05rem;
  }
  
  .action-button .v-icon {
    font-size: 22px !important;
  }
}

.v-card {
  transition: all 0.3s ease;
}

.action-button:focus {
  outline: 2px solid orange;
  outline-offset: 2px;
}
</style>
